import React, { useCallback, useMemo, useRef, useState } from 'react';
import FsLightbox from 'fslightbox-react';
import classNames from 'classnames';
import { getImageUrl } from '@common/utils/image';
import { IImage } from "@common/redux/types/images";
import { Picture } from "@kursk/components/ui/Picture/Picture";
import { PortalImage } from "@kursk/components/ui/PortalImage/PortalImage";
import { useMutationObservable } from "@common/hooks/useMutationObservable";
import styles from './ImagePortrait.module.scss';

const DEFAULT_ASPECT_RATIO = 3 / 2;
const PREVIEW_IMAGE_WIDTH = 600;
const PREVIEW_IMAGE_HEIGHT = 400;

interface ImageProps {
  image: IImage,
  useFullscreen?: boolean;
  className?: string;
  carouselSlides?: IImage[];
}

export const ImagePortrait = (
  { image, useFullscreen = false, className = '', carouselSlides = [] }: ImageProps) => {
  const ref = useRef();
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [showDescription, setShowDescription] = useState<boolean>(false)
  const [slide, setSlide] = useState<number>(null);
  const [description, setDescription] = useState<string>(null);

  const setCarouselImageState = () => {
    setFullscreen(!fullscreen)
    if (carouselSlides.length > 0) setSlide(carouselSlides.indexOf(image) + 1)
    setShowDescription(!showDescription)
    setDescription(image.description)
  }

  const onILightBoxClose = () => {
    setShowDescription(!showDescription)
  }

  /* Когда происходит листание картинки, то происходит мутация целевого узла,
  а именно, меняется текстовое значение счетчика картинки, которое расположено
  в левом верхнем углу. Таким образом мы можем отследить изменение значения счетчика,
  что позволяет рассчитать значение индекса картинки в carouselSlides и его description*/
  const onLightBoxMutation = useCallback((mutationList) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'characterData' && carouselSlides.length > 0) {
        setDescription(carouselSlides[parseInt(mutation.target.data) - 1].description)
      }
    })
  }, [carouselSlides])

  const LightBoxComponent = useMemo(() => {
    return (
      <FsLightbox
        toggler={fullscreen}
        type="image"
        sources={carouselSlides.length > 0 ? carouselSlides.map(slide => getImageUrl(slide)) : [getImageUrl(image)]}
        slide={carouselSlides.length > 0 ? slide : 1}
        onClose={onILightBoxClose}
      />
    )
  }, [slide, carouselSlides, fullscreen])

  const DescriptionComponent = useMemo(() => {
    return (
      <div className={styles.carouselImageDescription}>
        {description}
      </div>
    )
  }, [description])

  if (carouselSlides.length > 0) useMutationObservable({ targetEl: ref.current, cb: onLightBoxMutation });

  if (!image) {
    return <></>
  }

  let { crop: { width, height } = {} } = image;

  const isPortrait = Number((width / height).toFixed(2)) < DEFAULT_ASPECT_RATIO;

  if (isPortrait) {
    if (height > PREVIEW_IMAGE_HEIGHT) {
      const ratio = height / PREVIEW_IMAGE_HEIGHT;
      height = PREVIEW_IMAGE_HEIGHT;
      width = Math.round(width / ratio);
    }
  } else {
    if (width > PREVIEW_IMAGE_WIDTH) {
      const ratio = width / PREVIEW_IMAGE_WIDTH;
      width = PREVIEW_IMAGE_WIDTH;
      height = Math.round(height / ratio);
    }
  }

  if (!width) width = PREVIEW_IMAGE_WIDTH;
  if (!height) height = PREVIEW_IMAGE_HEIGHT;

  const srcSet = [{
    media: '(min-width: 0px)',
    width: width,
    height: height,
  }]

  return (
    <div className={className}>
      <div
        onClick={setCarouselImageState}
        className={classNames([styles.imgWrap, useFullscreen && styles.pointer])}
      >
        <Picture srcSet={srcSet} image={image}/>
      </div>
      {useFullscreen && <PortalImage>
        <div ref={ref}>
          {LightBoxComponent}
        </div>
        {showDescription && description && DescriptionComponent}
      </PortalImage>}
    </div>
  );
};
